import React, { Fragment, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { AuthUserLogin } from '../../../redux/actions/AuthAction';
import { useNavigate } from 'react-router-dom';

const LoginModal = ({ isOpen, closeModal }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const focusRef = useRef(null);

  // State management for the authentication flow
  const [isRegistred, setIsRegistred] = useState(false);
  const [otpIsSent, setOtpIsSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputFields, setInputFields] = useState({
    name: '',
    email: '',
    phone: '',
    otp: '',
  });

  // Handles changes to all form inputs
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setInputFields({ ...inputFields, [name]: value });
  };

  // Centralized function for handling successful authentication
  const handleSuccessfulAuth = (data, message) => {
    localStorage.setItem('accessToken', data.access_token);
    dispatch(AuthUserLogin(data));
    handleCloseModal();
    navigate('/dashboard/account');
    toast.success(message || 'Success!');
  };

  // Main form submission handler
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      // --- STEP 1: Handle SEND OTP request ---
      if (!otpIsSent) {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/send-otp`, {
          phone: inputFields.phone,
        });

        if (response.data.status) {
          toast.success("OTP sent successfully.");
          setIsRegistred(response.data.data.is_registred);
          setOtpIsSent(true);
        } else {
          toast.error(response.data.message);
        }
        
        // Stop the function here. The user will now fill the OTP and click submit again.
        setLoading(false);
        return;
      }

      // --- STEP 2: Handle LOGIN or REGISTER request ---
      let response;
      if (isRegistred) {
        // User exists, so perform LOGIN
        response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/login`, {
          phone: inputFields.phone,
          otp: inputFields.otp,
        });
      } else {
        // User does not exist, so perform REGISTER
        response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/register`, {
          name: inputFields.name,
          email: inputFields.email,
          phone: inputFields.phone,
          otp: inputFields.otp,
        });
      }

      // Handle the response from either login or register
      if (response.data.status) {
        handleSuccessfulAuth(response.data.data, response.data.message);
      } else {
        toast.error(response.data.message);
      }

    } catch (error) {
      // Improved error handling: Show specific error from backend if available
      const message = error.response?.data?.message || "Something went wrong. Please try again.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!inputFields.phone || inputFields.phone.length !== 10) {
      toast.error("Please enter a valid 10-digit phone number first.");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/send-otp`, {
        phone: inputFields.phone,
      });

      if (response.data.status) {
        toast.success("OTP resent successfully.");
      } else {
        toast.error(response.data.message || "Failed to resend OTP.");
      }
    } catch (error) {
      const message = error.response?.data?.message || "Error resending OTP.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Resets all state when the modal is closed
  const handleCloseModal = () => {
    setInputFields({ name: '', email: '', phone: '', otp: '' });
    setIsRegistred(false);
    setOtpIsSent(false);
    setLoading(false);
    closeModal();
  };

  return (
    <React.Fragment>
      <Transition appear as={Fragment} show={isOpen}>
        <Dialog as='div' className={'relative z-50'} onClose={handleCloseModal} initialFocus={focusRef}>
          <Transition.Child
            as={Fragment}
            enter='ease-in-out duration-300' enterFrom='opacity-0' enterTo='opacity-100'
            leave='ease-in-out duration-300' leaveFrom='opacity-100' leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black bg-opacity-50' />
          </Transition.Child>

          <div className='fixed inset-0 overflow-y-auto'>
            <div className='min-h-full flex items-center justify-center p-4 text-center'>
              <Transition.Child
                as={Fragment}
                enter='ease-in-out duration-300' enterFrom='opacity-0 scale-90' enterTo='opacity-100 scale-100'
                leave='ease-in-out duration-300' leaveFrom='opacity-100 scale-100' leaveTo='opacity-0 scale-90'
              >
                <Dialog.Panel className={"relative w-full max-w-sm bg-white max-h-[90vh] overflow-y-auto transform shadow-lg flex flex-col gap-10 lg:px-12 lg:py-14 sm:px-10 sm:py-12 text-left"} ref={focusRef}>
                  <form className='space-y-4' onSubmit={handleFormSubmit}>
                    <div className='text-center space-y-1 py-2'>
                      <h1 className='text-2xl font-semibold text-ascent-dark'>Welcome Back!</h1>
                      <p className='text-xs text-gray-600'>Enter your credentials to continue</p>
                    </div>

                    <div className='space-y-4'>
                      <div className='input-group'>
                        <label htmlFor="phone" className='input-label'>Phone <em>*</em></label>
                        <input 
                          type="tel" 
                          value={inputFields.phone} 
                          name='phone' 
                          required={true} 
                          className='input-box-md w-full' 
                          onChange={handleInputChange} 
                          placeholder='Phone (10 Digits)' 
                          minLength={10} 
                          maxLength={10} 
                          pattern='[0-9]{10}' 
                          readOnly={otpIsSent} // UX Improvement: Disable after OTP is sent
                        />
                      </div>

                      {otpIsSent && (
                        <Fragment>
                          <div className='input-group'>
                            <label htmlFor="otp" className='input-label'>Verification OTP <em>*</em></label>
                            <input type="tel" value={inputFields.otp} name='otp' required={true} className='input-box-md w-full' onChange={handleInputChange} placeholder='Enter OTP (4 Digits)' minLength={4} maxLength={4} pattern='[0-9]{4}' />
                            <div className="text-xs text-right mt-1">
                              <button
                                type="button"
                                onClick={handleResendOtp}
                                className="text-black mt-2 hover:underline disabled:opacity-50"
                                disabled={loading}
                              >
                                Resend OTP
                              </button>
                            </div>
                          </div>
                          {!isRegistred && (
                            <Fragment>
                              <div className='input-group'>
                                <label htmlFor="name" className='input-label'>Your Name <em>*</em></label>
                                <input type="text" value={inputFields.name} name='name' required={true} className='input-box-md w-full' onChange={handleInputChange} placeholder='Your Name' minLength={5} maxLength={100} />
                              </div>
                              <div className='input-group'>
                                <label htmlFor="email" className='input-label'>Email Address <em>*</em></label>
                                <input type="email" value={inputFields.email} name='email' required={true} className='input-box-md w-full' onChange={handleInputChange} placeholder='Email Address' />
                              </div>
                            </Fragment>
                          )}
                        </Fragment>
                      )}

                      <div>
                        <button type="submit" className='btn-primary-md w-full' disabled={loading}>
                          {loading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
                          ) : (
                            otpIsSent ? (isRegistred ? "Login" : "Register") : "Send OTP" // UX Improvement: Dynamic button text
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </React.Fragment>
  );
};

export default LoginModal;